import { parseDateRange } from '@dolfin/utils/dates'
import { type Diagram, DolfinDiagram } from './diagram'
import { Tables } from './db_types'
import LogicEvaluator from './logic-evaluator'
import { isBranchByUserNode } from './node/types'

export const evaluateBranchByUserLogic = (
  filter: { data?: unknown } | null | undefined,
  profileId: string,
  profileData: Tables<'organizations_profiles_data'>[],
  activePeriod: { start: string; end: string | undefined },
  queryPeriod?: { start: string; end: string }
) => {
  const logicEvaluator = new LogicEvaluator('deal', {})
  const sortedProfileData = [...(profileData ?? [])].sort((a, b) =>
    a.from_date.localeCompare(b.from_date)
  )
  const effectivePeriod = queryPeriod
    ? {
        start: activePeriod.start > queryPeriod.start ? activePeriod.start : queryPeriod.start,
        end: !activePeriod.end
          ? queryPeriod.end
          : activePeriod.end < queryPeriod.end
            ? activePeriod.end
            : queryPeriod.end
      }
    : activePeriod

  if (effectivePeriod.end && effectivePeriod.start > effectivePeriod.end) {
    return false
  }

  const dataAtPeriodStart = sortedProfileData
    ?.filter(p => p.from_date <= effectivePeriod.start)
    ?.slice(-1)?.[0]
  const pData = [
    dataAtPeriodStart,
    ...(sortedProfileData?.filter(
      p =>
        p.from_date >= effectivePeriod.start &&
        (!effectivePeriod.end || p.from_date <= effectivePeriod.end)
    ) || [])
  ].filter(Boolean)

  return (
    pData.some(
      p =>
        !!filter?.data &&
        logicEvaluator.evaluate(filter.data as Record<string, unknown>, {
          user: { id: profileId, type: p?.type }
        })
    ) ?? false
  )
}

export const getBranchByUserPathNumber = ({
  filters,
  profileId,
  profileData,
  activePeriod,
  queryPeriod
}: {
  filters: ({ data?: unknown } | null | undefined)[]
  profileId: string
  profileData: Tables<'organizations_profiles_data'>[]
  activePeriod: { start: string; end: string | undefined }
  queryPeriod?: { start: string; end: string }
}) => {
  for (const [index, filter] of filters.entries()) {
    if (!filter) {
      continue
    }

    if (
      !filter.data ||
      evaluateBranchByUserLogic(filter, profileId, profileData, activePeriod, queryPeriod)
    ) {
      return index
    }
  }

  return -1
}

export const doesVersionApplyToUser = (
  v: Pick<Tables<'compensation_rule_versions'>, 'diagram' | 'active_period'>,
  profileId: string,
  profileData: Tables<'organizations_profiles_data'>[],
  queryPeriod?: { start: string; end: string }
) => {
  const diagram = new DolfinDiagram(v.diagram as unknown as Diagram)
  const startNode = diagram.getStartNode()
  if (!startNode) {
    return true
  }

  const firstNode = diagram.getNextNode(diagram.outPaths(startNode)[0])
  if (!firstNode || !isBranchByUserNode(firstNode)) {
    return true
  }

  if (typeof v.active_period !== 'string') {
    return true
  }

  const activePeriod = parseDateRange(v.active_period)

  return (
    (firstNode.data.formData?.filters as { data?: unknown }[] | undefined)?.some(f =>
      evaluateBranchByUserLogic(f, profileId, profileData, activePeriod, queryPeriod)
    ) ?? false
  )
}
